<template>
  <section
    :class="['expandable-cards', `${variant}-cards`]"
    :style="boxStyle"
    :data-active="activeIndex"
    :aria-label="label"
  >
    <button
      v-for="(card, index) in cards"
      :key="card.title"
      :class="[
        'expand-card',
        {
          'has-image': Boolean(card.image),
          'is-first': index === 0,
          'is-expanded': index === activeIndex,
          'is-collapsed': index !== activeIndex,
        },
      ]"
      :style="cardStyle(index)"
      type="button"
      :aria-pressed="index === activeIndex"
      @click="selectCard(index)"
    >
      <img
        v-if="card.image"
        class="expand-card__image"
        :src="card.image"
        :alt="card.title"
        loading="lazy"
        decoding="async"
        draggable="false"
      />

      <span v-if="card.image && index !== activeIndex" class="expand-card__shade" aria-hidden="true" />

      <span class="expand-card__content">
        <span v-if="card.eyebrow && index === activeIndex" class="expand-card__eyebrow">
          {{ card.eyebrow }}
        </span>
        <span v-if="!card.eyebrow || index !== activeIndex" class="expand-card__title">
          {{ index === activeIndex ? card.title : card.collapsedTitle || card.title }}
        </span>
        <span v-if="index === activeIndex" class="expand-card__copy">
          {{ card.description }}
        </span>
      </span>
    </button>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { CSSProperties } from 'vue'

import {
  aboutCollapsedWidth,
  aboutExpandedWidth,
  aboutGap,
  creatorExpandedBox,
  creatorExpandedRightX,
  creatorSmallBoxes,
} from '../../data/home2026'
import type { DesignBox, ExpandCard } from '../../data/home2026'

const props = defineProps<{
  label: string
  box: DesignBox
  cards: ExpandCard[]
  scaledPx: (value: number) => string
  variant: 'about' | 'creator'
  activeIndex?: number
}>()

const emit = defineEmits<{
  'update:activeIndex': [value: number]
}>()

const internalActiveIndex = ref(props.activeIndex ?? 0)
const activeIndex = computed({
  get: () => props.activeIndex ?? internalActiveIndex.value,
  set: (value: number) => {
    internalActiveIndex.value = value
    emit('update:activeIndex', value)
  },
})

const designBoxStyle = (box: DesignBox): CSSProperties => ({
  left: props.scaledPx(box.x),
  top: props.scaledPx(box.y),
  width: props.scaledPx(box.w),
  height: props.scaledPx(box.h),
})

const boxStyle = computed<CSSProperties>(() => designBoxStyle(props.box))

const aboutCardStyle = (index: number): CSSProperties => {
  const active = activeIndex.value
  const width = index === active ? aboutExpandedWidth : aboutCollapsedWidth
  let x = 0

  for (let cardIndex = 0; cardIndex < index; cardIndex += 1) {
    const previousWidth = cardIndex === active ? aboutExpandedWidth : aboutCollapsedWidth
    x += previousWidth + aboutGap
  }

  return {
    left: props.scaledPx(x),
    top: '0px',
    width: props.scaledPx(width),
    height: '100%',
  }
}

const creatorCardStyle = (index: number): CSSProperties => {
  const active = activeIndex.value

  if (active === 0) {
    if (index === 0) return designBoxStyle(creatorExpandedBox)
    return designBoxStyle(creatorSmallBoxes[index - 1])
  }

  if (index === active) {
    return designBoxStyle({
      ...creatorExpandedBox,
      x: creatorExpandedRightX,
    })
  }

  const smallIndex = index === 0 ? 0 : 1
  return designBoxStyle({
    ...creatorSmallBoxes[smallIndex],
    x: 0,
  })
}

const cardStyle = (index: number) =>
  props.variant === 'about' ? aboutCardStyle(index) : creatorCardStyle(index)

const selectCard = (index: number) => {
  activeIndex.value = index
}
</script>

<style scoped>
.expandable-cards {
  position: absolute;
  z-index: 3;
  overflow: hidden;
  background: #000;
  contain: layout paint style;
}

.expand-card {
  position: absolute;
  overflow: hidden;
  padding: 0;
  border: 0;
  border-radius: var(--expand-card-radius);
  background: #fff;
  color: #000;
  cursor: pointer;
  font-family: 'Alibaba PuHuiTi 3.0', AlibabaPuHuiTi_3_55_Regular, PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-synthesis: none;
  text-align: left;
  -webkit-font-smoothing: antialiased;
  text-rendering: geometricprecision;
  transition:
    left 520ms cubic-bezier(0.19, 1, 0.22, 1),
    top 520ms cubic-bezier(0.19, 1, 0.22, 1),
    width 520ms cubic-bezier(0.19, 1, 0.22, 1),
    height 520ms cubic-bezier(0.19, 1, 0.22, 1),
    box-shadow 520ms cubic-bezier(0.19, 1, 0.22, 1),
    filter 520ms cubic-bezier(0.19, 1, 0.22, 1);
}

.expand-card.is-expanded {
  z-index: 3;
  box-shadow: 0 var(--expand-card-shadow-y) var(--expand-card-shadow-blur) rgb(0 0 0 / 22%);
}

.expand-card.is-collapsed {
  z-index: 1;
  filter: brightness(0.94);
}

.expand-card.is-collapsed.has-image {
  background: #070707;
}

.expand-card.is-collapsed:not(.has-image) {
  background: #070707;
}

.expand-card:focus-visible {
  outline: var(--focus-outline-width) solid rgb(138 188 249 / 90%);
  outline-offset: var(--focus-outline-offset);
}

.expand-card__image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 1;
  transition:
    opacity 280ms ease,
    transform 640ms cubic-bezier(0.19, 1, 0.22, 1);
  user-select: none;
  pointer-events: none;
}

.expand-card.is-expanded .expand-card__image {
  opacity: 0;
}

.expand-card.is-collapsed.has-image:hover .expand-card__image {
  transform: scale(1.06);
}

.expand-card__shade {
  position: absolute;
  z-index: 1;
  inset: 0;
  background: rgb(0 0 0 / 42%);
  pointer-events: none;
}

.about-cards .expand-card,
.creator-cards .expand-card {
  will-change: left, top, width, height;
}

.expand-card__content {
  position: absolute;
  z-index: 2;
  inset: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  margin: 0;
  letter-spacing: 0;
  overflow-wrap: break-word;
  pointer-events: none;
}

.about-cards .expand-card__content {
  padding: var(--about-padding-top) var(--about-padding-x);
}

.creator-cards .expand-card__content {
  padding: var(--creator-padding-top) var(--creator-padding-x);
}

.expand-card__eyebrow {
  font-family: AlibabaPuHuiTi_3_115_Black, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--about-mark-font);
  font-weight: 900;
  line-height: 1;
}

.expand-card__title {
  color: #000;
  font-family: AlibabaPuHuiTi_3_115_Black, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-weight: 900;
  line-height: 1;
}

.expand-card__copy {
  margin: 0;
  color: #000;
  font-family: AlibabaPuHuiTi_3_115_Black, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-weight: 900;
  transition:
    opacity 260ms ease,
    transform 520ms cubic-bezier(0.19, 1, 0.22, 1);
}

.about-cards .expand-card__copy {
  margin-top: var(--about-copy-top);
  font-family: AlibabaPuHuiTi_3_115_Black, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--about-copy-font);
  font-weight: 1000;
  line-height: var(--about-copy-line);
}

.about-cards .expand-card__title {
  font-size: var(--about-mark-font);
}

.about-cards .expand-card.is-collapsed .expand-card__content {
  justify-content: flex-start;
}

.about-cards .expand-card.is-collapsed .expand-card__title {
  color: #fff;
  font-size: var(--about-collapsed-mark-font);
  text-shadow: 0 2px 14px rgb(0 0 0 / 55%);
}

.about-cards .expand-card.is-collapsed:not(.has-image) .expand-card__title {
  color: #fff;
  text-shadow: none;
}

.about-cards .expand-card.is-collapsed.has-image .expand-card__title {
  display: none;
}

.creator-cards .expand-card__title {
  font-family: AlibabaPuHuiTi_3_85_Bold, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--creator-title-font);
  font-weight: 700;
  line-height: var(--creator-title-line);
}

.creator-cards .expand-card__copy {
  margin-top: var(--creator-copy-top);
  font-family: AlibabaPuHuiTi_3_85_Bold, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--creator-copy-font);
  font-weight: 700;
  line-height: var(--creator-copy-line);
}

.creator-cards .expand-card.is-collapsed .expand-card__content {
  padding: calc(var(--creator-padding-top) * 0.72) calc(var(--creator-padding-x) * 0.55);
}

.creator-cards .expand-card.is-collapsed .expand-card__title {
  color: #fff;
  font-size: calc(var(--creator-title-font) * 0.72);
  text-shadow: 0 2px 16px rgb(0 0 0 / 62%);
}

.creator-cards .expand-card.is-collapsed:not(.has-image) .expand-card__title {
  color: #fff;
  text-shadow: none;
}

.creator-cards .expand-card.is-collapsed.has-image.is-first .expand-card__title {
  display: none;
}

.about-cards .expand-card.is-collapsed.has-image .expand-card__shade,
.creator-cards .expand-card.is-collapsed.has-image.is-first .expand-card__shade {
  display: none;
}

.expand-card.is-collapsed .expand-card__copy {
  opacity: 0;
  visibility: hidden;
  transform: translateY(8%);
}

@media (prefers-reduced-motion: reduce) {
  .expand-card,
  .expand-card__copy,
  .expand-card__image {
    transition: none;
  }
}
</style>
